/**
 * Magic Link API Client
 *
 * ISO 27001 A.9.4.2 - Secure log-on procedures
 * API pour authentification passwordless via magic links
 */

import { safeFetch } from '@/utils/api';

const API_BASE = '/api/v1/core/auth/magic-link';

export interface MagicLinkRequestResult {
  success: boolean;
  message: string;
  expiresIn?: number; // seconds
}

export interface MagicLinkVerifyResult {
  success: boolean;
  message?: string;
  accessToken?: string;
  refreshToken?: string;
  user?: {
    id: string;
    email: string;
    fullName: string;
    role: string;
    tenantId: string;
    companyId: string;
  };
  expiresIn?: number;
}

export const magicLinkApi = {
  /**
   * Request a magic link to be sent to email
   * Rate limited: 3 per user/15min, 10 per IP/15min
   */
  async requestMagicLink(email: string): Promise<MagicLinkRequestResult> {
    const response = await safeFetch(`${API_BASE}/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Erreur lors de l\'envoi du lien');
    }

    return response.json();
  },

  /**
   * Resend magic link (same rate limits apply)
   */
  async resendMagicLink(email: string): Promise<MagicLinkRequestResult> {
    const response = await safeFetch(`${API_BASE}/resend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Erreur lors du renvoi du lien');
    }

    return response.json();
  },

  /**
   * Verify magic link token and get auth tokens
   */
  async verifyMagicLink(token: string): Promise<MagicLinkVerifyResult> {
    const response = await safeFetch(`${API_BASE}/verify?token=${encodeURIComponent(token)}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Lien invalide ou expiré');
    }

    return response.json();
  },
};

export default magicLinkApi;
